import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export interface ColumnConfig {
  header: string;
  key: string;
  width?: number;
  format?: string;
  style?: any;
}

export interface ExcelStyle {
  font?: {
    bold?: boolean;
    color?: { rgb: string };
    name?: string;
    sz?: number;
  };
  fill?: {
    fgColor?: { rgb: string }; // This property sets the background color
  };
  alignment?: {
    horizontal?: string;
    vertical?: string;
  };
  border?: {
    top?: { style: string; color?: { rgb: string } };
    bottom?: { style: string; color?: { rgb: string } };
    left?: { style: string; color?: { rgb: string } };
    right?: { style: string; color?: { rgb: string } };
  };
  numFmt?: string;
}

export interface ExcelConfig {
  sheetName: string;
  fileName: string;
  columns: ColumnConfig[];
  groupBy?: string;
  showBalance?: boolean;
  balanceColumns?: string[];
  customStyles?: { [key: string]: ExcelStyle };
  headerStyle?: ExcelStyle;
  cellStyle?: ExcelStyle;
  balanceStyle?: ExcelStyle;
}

@Injectable({
  providedIn: 'root',
})
export class ExcelExportService {
  private config!: ExcelConfig;
  private worksheet!: XLSX.WorkSheet;

  exportToExcel(data: any[], config: ExcelConfig) {
    if (!data || !config) {
      return;
    }

    this.config = {
      ...config,
      headerStyle: config.headerStyle || {
        fill: { fgColor: { rgb: 'FF0000' } },
      }, // Red background
      cellStyle: {
        alignment: { horizontal: 'right' },
        ...config.cellStyle,
      },
      balanceStyle: config.balanceStyle || {},
    };

    const workbook = XLSX.utils.book_new();
    const wsData: any[] = [];

    if (this.config.groupBy) {
      const groups = this.groupData(data, this.config.groupBy);
      Object.entries(groups).forEach(([groupValue, groupData], index) => {
        wsData.push([groupValue]);
        this.addHeaders(wsData);
        groupData.forEach((item) => this.addDataRow(wsData, item));

        if (this.config.showBalance && this.config.balanceColumns) {
          this.addBalanceRow(wsData, groupData);
        }

        if (index < Object.keys(groups).length - 1) {
          wsData.push([]);
        }
      });
    } else {
      this.addHeaders(wsData);
      data.forEach((item) => this.addDataRow(wsData, item));

      if (this.config.showBalance && this.config.balanceColumns) {
        this.addBalanceRow(wsData, data);
      }
    }

    this.worksheet = XLSX.utils.aoa_to_sheet(wsData);
    this.applyHeaderStyle();
    if (this.config.cellStyle) {
      for (let rowIndex = 1; rowIndex < wsData.length; rowIndex++) {
        wsData[rowIndex].forEach((_: any, colIndex: any) => {
          const cellAddress = XLSX.utils.encode_cell({
            c: colIndex,
            r: rowIndex,
          });
          if (this.worksheet[cellAddress]) {
            this.worksheet[cellAddress].s = this.config.cellStyle;
          }
        });
      }
    }

    if (this.config.columns) {
      this.worksheet['!cols'] = this.config.columns.map((col) => ({
        wch: col.width || 12,
      }));
    }

    XLSX.utils.book_append_sheet(
      workbook,
      this.worksheet,
      this.config.sheetName,
    );

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${this.config.fileName}.xlsx`);
  }

  private groupData(data: any[], groupBy: string): { [key: string]: any[] } {
    return data.reduce((groups: { [key: string]: any[] }, item) => {
      const key = item[groupBy] || '';
      groups[key] = groups[key] || [];
      groups[key].push(item);
      return groups;
    }, {});
  }

  private addHeaders(wsData: any[]) {
    if (this.config.columns) {
      wsData.push(this.config.columns.map((col) => col.header));
    }
  }

  private addDataRow(wsData: any[], item: any) {
    if (this.config.columns) {
      wsData.push(this.config.columns.map((col) => item[col.key]));
    }
  }

  private addBalanceRow(wsData: any[], data: any[]) {
    if (!this.config.columns || !this.config.balanceColumns) return;

    const balanceRow = new Array(this.config.columns.length).fill('');
    balanceRow[0] = 'Balance';

    this.config.balanceColumns.forEach((col) => {
      const total = data.reduce(
        (sum, item) => sum + (Number(item[col]) || 0),
        0,
      );
      const colIndex =
        this.config.columns?.findIndex((c) => c.key === col) ?? -1;
      if (colIndex !== -1) {
        balanceRow[colIndex] = total;
      }
    });

    wsData.push(balanceRow);
  }

  private getFormattedDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  private applyHeaderStyle() {
    if (this.config.headerStyle) {
      this.config.columns.forEach((col, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ c: colIndex, r: 0 });
        if (this.worksheet[cellAddress]) {
          this.worksheet[cellAddress].s = this.config.headerStyle;
        }
      });
    }
  }
}
